// Build and print/export a filled boat yard document from a record: the ENTRY form or the EXIT form, in
// Greek (ΕΝΤΥΠΟ ΕΙΣΑΓΩΓΗΣ / ΕΞΑΓΩΓΗΣ ΣΚΑΦΟΥΣ), English, or both on one sheet (the words live in
// boatDocCopy.ts). Opens a clean
// A4-portrait window and triggers the print dialog (which also offers Save as PDF), under the same
// TECHNOHULL logo as the in-app .doc-sheet so an exported copy matches the printable Form tab. Called synchronously from a
// click, so the popup is not blocked; the window is printed from the app once it has loaded (printWindow.ts).

import type { BoatDocRecord } from '@technohull/shared';
import { boatDocView, type BoatDocKind, type DocLang, type DocLine } from './boatDocCopy.js';
import { printInWindow } from './printWindow.js';

export type { BoatDocKind } from './boatDocCopy.js';

const esc = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** a piece of text: its first line as is, a translation line under it in the quieter `alt` style. */
const linesHtml = (lines: DocLine[], tag: string, cls: string) =>
  lines.map((l, i) => `<${tag} class="${cls}${i ? ' alt' : ''}" lang="${l.lang}">${esc(l.text)}</${tag}>`).join('');

/** the filled form as a standalone A4 page. */
export function boatDocHtml(
  kind: BoatDocKind,
  rec: BoatDocRecord,
  fmtDate: (input: string | number | Date | null | undefined) => string,
  lang: DocLang = 'both',
): string {
  const dateIso = kind === 'entry' ? rec.entryDate : rec.exitDate;
  const dateStr = dateIso ? fmtDate(dateIso) : '';
  const logo = `${window.location.origin}/technohull-logo-doc.png`;
  const c = boatDocView(lang, kind, { date: dateStr, boatName: rec.boatName, model: rec.model, sap: rec.sap, phone: rec.phone, hin: rec.hin });

  const tbody = c.rows
    .map((r) => `<tr><td class="k">${linesHtml(r.label, 'span', 'kl')}${r.hint ? `<small>${esc(r.hint)}</small>` : ''}</td><td class="v">${esc(r.value) || '&nbsp;'}</td></tr>`)
    .join('');

  return `<!doctype html><html lang="${c.lang}"><head><meta charset="utf-8">
<title>${esc(c.title.map((l) => l.text).join(' / '))}${rec.boatName ? ' - ' + esc(rec.boatName) : ''}</title>
<style>
  *{-webkit-print-color-adjust:exact;print-color-adjust:exact;box-sizing:border-box}
  html,body{margin:0;padding:0}
  body{font-family:Arial,Helvetica,sans-serif;color:#141432;background:#fff}
  .sheet{max-width:760px;margin:0 auto;padding:34px 44px 44px}
  .brand{display:block;height:54px;width:auto}
  h1{font-size:19px;font-weight:800;letter-spacing:.01em;margin:22px 0 0}
  .t.alt{display:block;font-size:13px;font-weight:700;letter-spacing:.06em;color:#55566a;margin-top:3px}
  .rule{height:2px;background:#9a3b2f;border:0;margin:10px 0 20px}
  .ih{font-weight:700;text-decoration:underline;margin:0 0 6px}
  .instr{font-size:13px;line-height:1.5;color:#2a2a3a;margin:0 0 22px;max-width:62ch}
  .instr:has(+ .instr){margin-bottom:8px}
  .instr.alt{color:#55566a;font-style:italic}
  table.f{width:100%;border-collapse:collapse;margin-bottom:34px}
  table.f td{border:1px solid #2a2a3a;padding:13px 12px;vertical-align:middle}
  table.f td.k{width:36%;font-weight:700;background:#f6f6f9}
  table.f td.k .kl{display:block}
  table.f td.k .kl.alt{font-weight:400;font-size:11px;color:#55566a;margin-top:1px}
  table.f td.k small{display:block;font-weight:400;font-size:10px;color:#9a3b2f;margin-top:2px}
  table.f td.v{font-size:15px}
  .sign{display:flex;justify-content:flex-end;margin-top:46px}
  .sign .box{width:300px;text-align:center}
  .sign .line{border-top:1px solid #2a2a3a;margin-bottom:6px;height:1px}
  .sign .cap{display:block;font-size:12px;color:#2a2a3a}
  .sign .cap.alt{font-size:11px;color:#55566a;margin-top:1px}
  @page{size:A4 portrait;margin:14mm}
</style></head><body>
<div class="sheet">
  <img class="brand" src="${logo}" alt="TECHNOHULL - Seaborn Performance">
  <h1>${linesHtml(c.title, 'span', 't')}</h1><hr class="rule">
  <div class="ih">${esc(c.instrHeading)}</div>
  ${linesHtml(c.instr, 'p', 'instr')}
  <table class="f"><tbody>${tbody}</tbody></table>
  <div class="sign"><div class="box"><div class="line"></div>${linesHtml(c.sign, 'span', 'cap')}</div></div>
</div>
</body></html>`;
}

/** open a print window with the filled form and trigger the print dialog. Returns false when the
 *  browser blocked the pop-up, so the caller can say so. */
export function printBoatDoc(
  kind: BoatDocKind,
  rec: BoatDocRecord,
  fmtDate: (input: string | number | Date | null | undefined) => string,
  lang: DocLang = 'both',
): boolean {
  return printInWindow(boatDocHtml(kind, rec, fmtDate, lang));
}
